import React from 'react';
import useProfileController from '../controllers/useProfileController';

function Profile() {
  const {
    profile,
    avatarUrl,
    previewUrl,
    loading,
    error,
    updateProfile,
    handleChange,
    handleFileUpload,
  } = useProfileController();

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;

  return (
    <div className='flex'>
      <section className="flex-1 py-12 px-5 bg-gray-100 dark:bg-gray-900">
        <div className=''>
          <h1 className="text-5xl font-extrabold text-center lg:text-7xl 2xl:text-8xl">
            <span className="text-transparent bg-gradient-to-br bg-clip-text from-sky-800 via-sky-600 to-sky-700">
              Profile
            </span>
          </h1>

          <p className="max-w-3xl mx-auto my-6 text-lg text-center text-gray-700 dark:text-white md:text-xl">
            {profile && Object.values(profile).every((value) => value === '')
              ? 'Welcome! Please fill in your profile information.'
              : 'Manage your profile information'}
          </p>
        </div>
        <form onSubmit={updateProfile} className='grid lg:grid-cols-3 sm:grid-cols-2 grid-cols-1 gap-5 my-10'>
            <div className="flex flex-col row-span-3 items-center justify-center w-full max-w-sm mx-auto px-2 py-2">
              <div
                className="w-full h-64 bg-gray-300 bg-center bg-cover rounded-lg shadow-md"
                style={{
                  backgroundImage: `url(${
                    previewUrl || avatarUrl || 'https://cdn.pixabay.com/photo/2018/11/13/21/43/avatar-3814049_1280.png'
                  })`,
                }}
              ></div>

              <div className="w-56 -mt-10 overflow-hidden bg-white rounded-lg shadow-lg md:w-64 dark:bg-gray-800">
                <h3 className="pt-1 tracking-wide font-bold text-center text-gray-800 dark:text-white">Photo</h3>

                <div className="dark:bg-gray-700">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileUpload}
                    disabled={loading}
                    className="block w-full mt-2 text-sm text-gray-600 bg-white border border-gray-200 rounded-lg file:bg-gray-200 file:text-gray-700 file:text-sm file:px-4 file:py-1 file:border-none file:rounded-full dark:file:bg-gray-800 dark:file:text-gray-200 dark:text-gray-300 placeholder-gray-400/70 dark:placeholder-gray-500 focus:border-blue-400 focus:outline-none focus:ring focus:ring-blue-300 focus:ring-opacity-40 dark:border-gray-600 dark:bg-gray-900 dark:focus:border-blue-300"
                  />
                </div>
              </div>
            </div>

            <div className='px-2 py-2'>
              <label htmlFor="fullname" className="block text-sm text-gray-500 dark:text-gray-300">Full Name</label>
              <input
                type="text"
                name="fullname"
                value={profile.fullname}
                onChange={handleChange}
                className="block bg-gray-50 mt-2 w-full placeholder-gray-400/70 dark:placeholder-gray-500 rounded-lg border border-gray-200 px-5 py-2.5 text-gray-700 focus:border-blue-400 focus:outline-none focus:ring focus:ring-blue-300 focus:ring-opacity-40 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-300 dark:focus:border-blue-300"
              />
            </div>

            <div className='px-2 py-2'>
              <label htmlFor="shortname" className="block text-sm text-gray-500 dark:text-gray-300">Short Name</label>
              <input
                type="text"
                name="shortname"
                value={profile.shortname}
                onChange={handleChange}
                className="block bg-gray-50 mt-2 w-full placeholder-gray-400/70 dark:placeholder-gray-500 rounded-lg border border-gray-200 px-5 py-2.5 text-gray-700 focus:border-blue-400 focus:outline-none focus:ring focus:ring-blue-300 focus:ring-opacity-40 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-300 dark:focus:border-blue-300"
              />
            </div>

            <div className='px-2 py-2'>
              <label htmlFor="country" className="block text-sm text-gray-500 dark:text-gray-300">Country</label>
              <input
                type="text"
                name="country"
                value={profile.country}
                onChange={handleChange}
                className="block bg-gray-50 mt-2 w-full placeholder-gray-400/70 dark:placeholder-gray-500 rounded-lg border border-gray-200 px-5 py-2.5 text-gray-700 focus:border-blue-400 focus:outline-none focus:ring focus:ring-blue-300 focus:ring-opacity-40 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-300 dark:focus:border-blue-300"
              />
            </div>

            <div className='px-2 py-2'>
              <label htmlFor="city" className="block text-sm text-gray-500 dark:text-gray-300">City</label>
              <input
                type="text"
                name="city"
                value={profile.city}
                onChange={handleChange}
                className="block bg-gray-50 mt-2 w-full placeholder-gray-400/70 dark:placeholder-gray-500 rounded-lg border border-gray-200 px-5 py-2.5 text-gray-700 focus:border-blue-400 focus:outline-none focus:ring focus:ring-blue-300 focus:ring-opacity-40 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-300 dark:focus:border-blue-300"
              />
            </div>

            <div className='px-2 py-2'>
              <label htmlFor="email" className="block text-sm text-gray-500 dark:text-gray-300">Email Address</label>
              <input
                type="email"
                name="email"
                value={profile.email}
                onChange={handleChange}
                className="mt-2 block w-full placeholder-gray-400/70 rounded-lg border border-gray-200 bg-white px-5 py-2.5 text-gray-700 focus:border-blue-400 focus:outline-none focus:ring focus:ring-blue-300 focus:ring-opacity-40 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-300 dark:focus:border-blue-300"
              />
            </div>

            <div className='px-2 py-2'>
              <label htmlFor="phone" className="block text-sm text-gray-500 dark:text-gray-300">Phone Number</label>
              <input
                type="text"
                name="phone"
                value={profile.phone}
                onChange={handleChange}
                className="block bg-gray-50 mt-2 w-full placeholder-gray-400/70 dark:placeholder-gray-500 rounded-lg border border-gray-200 px-5 py-2.5 text-gray-700 focus:border-blue-400 focus:outline-none focus:ring focus:ring-blue-300 focus:ring-opacity-40 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-300 dark:focus:border-blue-300"
              />
            </div>

            <div className='px-2 py-2'>
              <label htmlFor="personal_web" className="block text-sm text-gray-500 dark:text-gray-300">Personal Web</label>
              <input
                type="text"
                name="personal_web"
                value={profile.personal_web}
                onChange={handleChange}
                className="block bg-gray-50 mt-2 w-full placeholder-gray-400/70 dark:placeholder-gray-500 rounded-lg border border-gray-200 px-5 py-2.5 text-gray-700 focus:border-blue-400 focus:outline-none focus:ring focus:ring-blue-300 focus:ring-opacity-40 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-300 dark:focus:border-blue-300"
              />
            </div>
            
            <div className='px-2 py-2'>
              <label htmlFor="linkedin" className="block text-sm text-gray-500 dark:text-gray-300">Linkedin</label>
              <input
                type="text"
                name="linkedin"
                value={profile.linkedin}
                onChange={handleChange}
                className="block bg-gray-50 mt-2 w-full placeholder-gray-400/70 dark:placeholder-gray-500 rounded-lg border border-gray-200 px-5 py-2.5 text-gray-700 focus:border-blue-400 focus:outline-none focus:ring focus:ring-blue-300 focus:ring-opacity-40 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-300 dark:focus:border-blue-300"
              />
            </div>

            <div className='px-2 py-2'>
              <label htmlFor="github" className="block text-sm text-gray-500 dark:text-gray-300">Github</label>
              <input
                type="text"
                name="github"
                value={profile.github}
                onChange={handleChange}
                className="block bg-gray-50 mt-2 w-full placeholder-gray-400/70 dark:placeholder-gray-500 rounded-lg border border-gray-200 px-5 py-2.5 text-gray-700 focus:border-blue-400 focus:outline-none focus:ring focus:ring-blue-300 focus:ring-opacity-40 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-300 dark:focus:border-blue-300"
              />
            </div>

            <div className='px-2 py-2'>
              <label htmlFor="behance" className="block text-sm text-gray-500 dark:text-gray-300">Behance</label>
              <input
                type="text"
                name="behance"
                value={profile.behance}
                onChange={handleChange}
                className="block bg-gray-50 mt-2 w-full placeholder-gray-400/70 dark:placeholder-gray-500 rounded-lg border border-gray-200 px-5 py-2.5 text-gray-700 focus:border-blue-400 focus:outline-none focus:ring focus:ring-blue-300 focus:ring-opacity-40 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-300 dark:focus:border-blue-300"
              />
            </div>

            <div className='px-2 py-2'>
              <label htmlFor="instagram" className="block text-sm text-gray-500 dark:text-gray-300">Instagram</label>
              <input
                type="text"
                name="instagram"
                value={profile.instagram}
                onChange={handleChange}
                className="block bg-gray-50 mt-2 w-full placeholder-gray-400/70 dark:placeholder-gray-500 rounded-lg border border-gray-200 px-5 py-2.5 text-gray-700 focus:border-blue-400 focus:outline-none focus:ring focus:ring-blue-300 focus:ring-opacity-40 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-300 dark:focus:border-blue-300"
              />
            </div>

            <div className='px-2 py-2'>
              <label htmlFor="youtube" className="block text-sm text-gray-500 dark:text-gray-300">Youtube</label>
              <input
                type="text"
                name="youtube"
                value={profile.youtube}
                onChange={handleChange}
                className="block bg-gray-50 mt-2 w-full placeholder-gray-400/70 dark:placeholder-gray-500 rounded-lg border border-gray-200 px-5 py-2.5 text-gray-700 focus:border-blue-400 focus:outline-none focus:ring focus:ring-blue-300 focus:ring-opacity-40 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-300 dark:focus:border-blue-300"
              />
            </div>

            <div className='flex px-2 py-2'>
              <button
                type="submit"
                disabled={loading}
                className="w-full px-4 py-2 my-4 content-center tracking-wide text-white capitalize transition-colors duration-300 transform bg-blue-500 rounded-lg hover:bg-blue-600 focus:outline-none focus:ring focus:ring-blue-300 focus:ring-opacity-80"
              >
                {loading ? 'Updating...' : 'Update Profile'}
              </button>
            </div>
        </form>
      </section>
    </div>
  );
}

export default Profile;